import React, { useEffect, useState } from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  PDFViewer,
  Image,
} from "@react-pdf/renderer";

import image from "../../../../assets/img/marifer.png";
import { dataTransform } from "./functions";

const C = {
  pink: "#f43f5e",
  pinkLight: "#fce7f3",
  pinkMid: "#f9a8d4",
  pinkDark: "#be185d",
  gray: "#6b7280",
  dark: "#374151",
  white: "#ffffff",
};

// Centimetros raw → Metros para mostrar
const fmtInsumo = (cantidad, unidad) => {
  const u = (unidad || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  if (u.includes("centimetro")) {
    return { qty: (cantidad / 100).toFixed(2), unit: "Metros" };
  }
  return { qty: Math.round(cantidad * 100) / 100, unit: unidad || "" };
};

const styles = StyleSheet.create({
  viewer: { width: "100%", height: window.innerHeight },
  page: { padding: 28, fontSize: 8, fontFamily: "Helvetica", backgroundColor: C.white },
  header: {
    flexDirection: "row", alignItems: "center",
    borderBottom: "2px solid #f43f5e", paddingBottom: 10, marginBottom: 14,
  },
  logoImg: { width: 55, height: 55, marginRight: 12 },
  headerTitle: { fontSize: 16, fontFamily: "Helvetica-Bold", color: C.pink, marginBottom: 2 },
  headerSub: { fontSize: 8, color: C.gray },
  headerDate: { fontSize: 7, color: C.gray, marginTop: 1 },
  infoBar: { flexDirection: "row", marginBottom: 14, gap: 6 },
  infoBox: {
    flex: 1, border: "1px solid #fce7f3", borderRadius: 4,
    padding: 6, backgroundColor: "#fff0f6",
  },
  infoLabel: { fontSize: 6, color: C.pinkDark, fontFamily: "Helvetica-Bold", marginBottom: 2, textTransform: "uppercase" },
  infoValue: { fontSize: 9, color: C.dark, fontFamily: "Helvetica-Bold" },
  sectionHeader: {
    flexDirection: "row", backgroundColor: "#f43f5e",
    borderRadius: 3, padding: "5px 8px", marginBottom: 2, marginTop: 10,
  },
  sectionHeaderText: { color: C.white, fontFamily: "Helvetica-Bold", fontSize: 8 },
  tableHead: {
    flexDirection: "row", backgroundColor: "#fce7f3",
    padding: "4px 0", borderBottom: "1px solid #f9a8d4",
  },
  tableHeadText: { fontSize: 7, fontFamily: "Helvetica-Bold", color: C.pinkDark, paddingHorizontal: 4 },
  tableRow: { flexDirection: "row", borderBottom: "1px solid #fce7f3", padding: "4px 0" },
  tableRowAlt: { flexDirection: "row", borderBottom: "1px solid #fce7f3", padding: "4px 0", backgroundColor: "#fff0f6" },
  cellText: { fontSize: 7.5, color: C.dark, paddingHorizontal: 4 },
  footer: {
    flexDirection: "row", justifyContent: "space-between",
    marginTop: 14, borderTop: "1px solid #fce7f3", paddingTop: 6,
  },
  footerText: { fontSize: 6.5, color: "#f9a8d4" },
  mermaBox: {
    marginTop: 8, padding: "5px 8px", backgroundColor: "#fff0f6",
    border: "1px solid #f9a8d4", borderRadius: 4,
  },
  mermaText: { fontSize: 8, color: C.pinkDark, fontFamily: "Helvetica-Bold" },
});

const fmt = (v) => (v ? String(v) : "-");
const fmtDate = (s) => (s ? s.split(" ")[0] : "-");

const OrderSummaryPDF = ({ defaultDataDetail: d }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    const convert = async () => {
      const res = await dataTransform(d);
      setData(res);
    };
    convert();
  }, [d]);

  if (!data) return "Calculando...";

  const pct = data.percentage;
  const withPct = (n) =>
    pct && pct > 0 ? Math.ceil(Number(n) + (Number(n) * pct) / 100) : Number(n);

  return (
    <PDFViewer style={styles.viewer}>
      <Document>
        <Page size="LETTER" style={styles.page}>

          {/* HEADER */}
          <View style={styles.header}>
            <Image style={styles.logoImg} src={image} />
            <View>
              <Text style={styles.headerTitle}>Resumen de Pedido</Text>
              <Text style={styles.headerSub}>Sistema de gestion Marifer</Text>
              <Text style={styles.headerDate}>
                Generado: {new Date().toLocaleDateString("es-MX")}
              </Text>
            </View>
          </View>

          {/* INFO BAR */}
          <View style={styles.infoBar}>
            {[
              { label: "Codigo", value: fmt(data.codigo) },
              { label: "Total piezas", value: String(Math.ceil(data.total_product)) },
              { label: "Creacion", value: fmtDate(data.fecha_creacion) },
              { label: "Entrega", value: fmtDate(data.fecha_entrega) },
              { label: "Est. termino", value: fmtDate(data.fecha_estimada) },
            ].map((item, i) => (
              <View key={i} style={styles.infoBox}>
                <Text style={styles.infoLabel}>{item.label}</Text>
                <Text style={styles.infoValue}>{item.value}</Text>
              </View>
            ))}
          </View>

          {/* PRODUCTOS */}
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionHeaderText}>PRODUCTOS</Text>
          </View>
          <View style={styles.tableHead}>
            <Text style={[styles.tableHeadText, { width: "22%" }]}>Nombre</Text>
            <Text style={[styles.tableHeadText, { width: "11%" }]}>Modelo</Text>
            <Text style={[styles.tableHeadText, { width: "10%" }]}>Total</Text>
            <Text style={[styles.tableHeadText, { width: "33%" }]}>Tallas</Text>
            <Text style={[styles.tableHeadText, { width: "24%" }]}>Prendas</Text>
          </View>
          {data.products.length === 0 ? (
            <View style={styles.tableRow}>
              <Text style={[styles.cellText, { width: "100%" }]}>Sin datos.</Text>
            </View>
          ) : (
            data.products.map((p, idx) => (
              <View key={idx} style={idx % 2 === 0 ? styles.tableRow : styles.tableRowAlt}>
                <Text style={[styles.cellText, { width: "22%" }]}>{fmt(p.nombre)}</Text>
                <Text style={[styles.cellText, { width: "11%" }]}>{fmt(p.modelo)}</Text>
                <Text style={[styles.cellText, { width: "10%" }]}>{withPct(p.cantidad)}</Text>
                {/* Tallas segmentadas: una por fila */}
                <View style={{ width: "33%", flexDirection: "row", flexWrap: "wrap", paddingHorizontal: 4 }}>
                  {(p.tallas || []).map((t, ti) => (
                    <View
                      key={ti}
                      style={{
                        flexDirection: "row", alignItems: "center",
                        backgroundColor: ti % 2 === 0 ? "#fff0f6" : "#fce7f3",
                        borderRadius: 3, marginRight: 3, marginBottom: 2,
                        paddingHorizontal: 4, paddingVertical: 1,
                      }}
                    >
                      <Text style={{ fontSize: 6.5, fontFamily: "Helvetica-Bold", color: "#be185d", marginRight: 5 }}>
                        {t.talla.nombre}
                      </Text>
                      <Text style={{ fontSize: 7, fontFamily: "Helvetica-Bold", color: "#374151" }}>
                        {withPct(t.cantidad)}
                      </Text>
                    </View>
                  ))}
                </View>
                <Text style={[styles.cellText, { width: "24%" }]}>
                  {(p.prenda || []).map((pr) => pr.nombre).join(", ")}
                </Text>
              </View>
            ))
          )}

          {/* INSUMOS */}
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionHeaderText}>INSUMOS REQUERIDOS</Text>
          </View>
          <View style={styles.tableHead}>
            <Text style={[styles.tableHeadText, { width: "55%" }]}>Nombre</Text>
            <Text style={[styles.tableHeadText, { width: "20%" }]}>Cantidad</Text>
            <Text style={[styles.tableHeadText, { width: "25%" }]}>Unidad</Text>
          </View>
          {data.insumosGrouped.length === 0 ? (
            <View style={styles.tableRow}>
              <Text style={[styles.cellText, { width: "100%" }]}>Sin datos.</Text>
            </View>
          ) : (
            [...data.insumosGrouped]
              .sort((a, b) => a.nombre.localeCompare(b.nombre))
              .map((item, idx) => {
                const { qty, unit } = fmtInsumo(item.cantidad, item.unidad);
                const esCm = (item.unidad || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").includes("centimetro");
                return (
                  <View key={idx}>
                    {/* Fila principal */}
                    <View style={idx % 2 === 0 ? styles.tableRow : styles.tableRowAlt}>
                      <Text style={[styles.cellText, { width: "55%", fontFamily: "Helvetica-Bold" }]}>{item.nombre}</Text>
                      <Text style={[styles.cellText, { width: "20%", fontFamily: "Helvetica-Bold" }]}>{qty}</Text>
                      <Text style={[styles.cellText, { width: "25%", fontFamily: "Helvetica-Bold" }]}>{unit}</Text>
                    </View>
                    {/* Desglose por talla para insumos en cm */}
                    {esCm && item.hasTalla && item.detallesPorTalla && (
                      <View style={{ paddingLeft: 12, paddingBottom: 2, backgroundColor: "#fdf2f8" }}>
                        {/* Encabezado sub-tabla */}
                        <View style={{ flexDirection: "row", borderBottom: "1px solid #fce7f3", paddingVertical: 1 }}>
                          <Text style={{ fontSize: 6, color: "#be185d", fontFamily: "Helvetica-Bold", width: "20%" }}>Talla</Text>
                          <Text style={{ fontSize: 6, color: "#be185d", fontFamily: "Helvetica-Bold", width: "25%" }}>Piezas</Text>
                          <Text style={{ fontSize: 6, color: "#be185d", fontFamily: "Helvetica-Bold", width: "25%" }}>cm/pieza</Text>
                          <Text style={{ fontSize: 6, color: "#be185d", fontFamily: "Helvetica-Bold", width: "30%" }}>Total (m)</Text>
                        </View>
                        {item.detallesPorTalla
                          .sort((a, b) => (a.talla || "").localeCompare(b.talla || ""))
                          .map((det, di) => (
                            <View key={di} style={{ flexDirection: "row", paddingVertical: 1, borderBottom: "1px solid #fce7f3" }}>
                              <Text style={{ fontSize: 6.5, color: "#374151", width: "20%" }}>{det.talla}</Text>
                              <Text style={{ fontSize: 6.5, color: "#374151", width: "25%" }}>{det.piezas}</Text>
                              <Text style={{ fontSize: 6.5, color: "#374151", width: "25%" }}>{det.cmPorPieza != null ? det.cmPorPieza + " cm" : "—"}</Text>
                              <Text style={{ fontSize: 6.5, color: "#f43f5e", fontFamily: "Helvetica-Bold", width: "30%" }}>{(det.cm / 100).toFixed(2) + " m"}</Text>
                            </View>
                          ))}
                      </View>
                    )}
                  </View>
                );
              })
          )}

          {/* CONSUMO DE TELA POR PRENDA */}
          {data.clothConsumption.length > 0 && (
            <>
              <View style={styles.sectionHeader}>
                <Text style={styles.sectionHeaderText}>CONSUMO DE TELA POR PRENDA</Text>
              </View>
              <View style={styles.tableHead}>
                <Text style={[styles.tableHeadText, { width: "35%" }]}>Tela</Text>
                <Text style={[styles.tableHeadText, { width: "25%" }]}>Prenda</Text>
                <Text style={[styles.tableHeadText, { width: "20%" }]}>Cant. promedio</Text>
                <Text style={[styles.tableHeadText, { width: "20%" }]}>Consumo estimado</Text>
              </View>
              {data.clothConsumption.map((m, idx) => (
                <View key={idx} style={idx % 2 === 0 ? styles.tableRow : styles.tableRowAlt}>
                  <Text style={[styles.cellText, { width: "35%" }]}>
                    {"(" + m.cloth.code + ") " + m.cloth.name + ", " + m.cloth.color + ", " + m.cloth.tone}
                  </Text>
                  <Text style={[styles.cellText, { width: "25%" }]}>{m.prenda.nombre}</Text>
                  <Text style={[styles.cellText, { width: "20%" }]}>{m.quantity + " " + m.unit}</Text>
                  <Text style={[styles.cellText, { width: "20%" }]}>{Math.ceil(m.clothTotal) + " m"}</Text>
                </View>
              ))}
            </>
          )}

          {/* CONSUMO DE TELA GENERAL */}
          {data.clothGrouped.length > 0 && (
            <>
              <View style={styles.sectionHeader}>
                <Text style={styles.sectionHeaderText}>CONSUMO DE TELA GENERAL</Text>
              </View>
              <View style={styles.tableHead}>
                <Text style={[styles.tableHeadText, { width: "60%" }]}>Tela</Text>
                <Text style={[styles.tableHeadText, { width: "40%" }]}>Total en metros</Text>
              </View>
              {data.clothGrouped.map((m, idx) => (
                <View key={idx} style={idx % 2 === 0 ? styles.tableRow : styles.tableRowAlt}>
                  <Text style={[styles.cellText, { width: "60%" }]}>{m.nombre}</Text>
                  <Text style={[styles.cellText, { width: "40%" }]}>{Math.ceil(m.quantity) + " m"}</Text>
                </View>
              ))}
            </>
          )}

          {/* NOTA MERMA */}
          {pct && pct > 0 && (
            <View style={styles.mermaBox}>
              <Text style={styles.mermaText}>
                {"NOTA: Se incluye un " + pct + "% de merma en todas las cantidades."}
              </Text>
            </View>
          )}

          {/* FOOTER */}
          <View style={styles.footer}>
            <Text style={styles.footerText}>{"(c)Marifer " + new Date().getFullYear()}</Text>
            <Text style={styles.footerText}>
              {"Pedido " + fmt(data.codigo) + " - " + new Date().toLocaleDateString("es-MX")}
            </Text>
          </View>

        </Page>
      </Document>
    </PDFViewer>
  );
};

export default OrderSummaryPDF;
